import chroma from "chroma-js";
import { hexToCSSFilter } from "hex-to-css-filter";

import { isEmpty } from "@base/common/libs/utils/utils.js";
import { KEY_PRIMARY_500 } from "@base/common/constants/index.js";
import { COMPUTE_FUNCTION_NAMES } from "./constants/index.js";
import { getS3FileKey } from "../utils/attachment.js";
import { Theme } from "./index.js";

let colorCache = {};

export function darken(color, perc) {
  if (!colorCache[`${color}:${perc}`]) {
    colorCache[`${color}:${perc}`] = chroma(color).darken(perc).hex();
  }
  return colorCache[`${color}:${perc}`];
}

export function brighten(color, perc) {
  if (!colorCache[`${color}:${perc}`]) {
    colorCache[`${color}:${perc}`] = chroma(color).brighten(perc).hex();
  }
  return colorCache[`${color}:${perc}`];
}

export function toFilter(color) {
  if (!colorCache[color]) {
    colorCache[color] = hexToCSSFilter(chroma(color).hex()).filter;
  }
  return colorCache[color];
}

export function palette(color) {
  if (!colorCache[color]) {
    colorCache[color] = chroma
      .scale(["white", color, "black"])
      .colors(11)
      .slice(1, 9);
  }
  return colorCache[color];
}

export async function isCSSFile(url) {
  const resp = await fetch(url);
  const content = await resp.text();
  const contentType = resp.headers.get("content-type");
  return contentType === "text/css" || content.indexOf("@font-face") >= 0
    ? content
    : false;
}

export const getValue = (key, meta, autoPriority) => {
  const obj = meta[key];
  if (!obj) {
    throw new Error(`key ${key} not found in meta.`);
  }
  if (obj.value) {
    return obj.value;
  }
  if (!autoPriority && obj.ref) {
    return getValue(obj.ref, meta);
  }
  if (obj.valueFunc) {
    return obj.valueFunc(meta);
  }
  if (autoPriority && obj.ref) {
    return getValue(obj.ref, meta);
  }
  return obj.defaultValue;
};

// converts app-COLOR-var1 to App.Color.Var1
export function getDotNotation(hypenedId) {
  hypenedId = hypenedId.toLowerCase();
  const dotParts = hypenedId.split(/[-_]/);
  const dotString = dotParts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(".");
  return dotString;
}

// converts var(--kf-color-primary-100) to Color.Primary.100
export function convertVariableToDotNotation(input) {
  input = input.replace("var(--kf-", "");
  input = input.replace(")", "");
  return getDotNotation(input);
}

export function convertDotToSnake(dotNotation) {
  return `--kf-${dotNotation.toLowerCase().replaceAll(".", "-")}`;
}

export function convertToCSSVar(string) {
  return `var(${convertDotToSnake(string)})`;
}

export function getStyleString(styleObj, options = {}) {
  let { idSuffix = "", themeModel, parentSelector = "" } = options;
  let styleString = "";

  for (let styleId in styleObj) {
    let value = styleObj[styleId];
    let styleName = `${parentSelector ? parentSelector + " " : ""}#${styleId}${
      idSuffix ? "-" + idSuffix : ""
    }`;
    styleString += `${styleName} {\n`;

    for (let cssProperty in value) {
      if (!value[cssProperty]) continue;

      let cssValue = getStyleValue({ value, cssProperty, themeModel });

      styleString += `${convertDotToSnake(cssProperty)}: ${cssValue}; \n`;
    }

    styleString += `}\n`;
  }

  return styleString;
}

export function getStyleValue({ value, cssProperty, themeModel }) {
  let cssValue;
  if (cssProperty.endsWith(".Shadow") && value[cssProperty]?.value) {
    cssValue = resolveShadowValue(value[cssProperty].value);
  } else if (value[cssProperty].ref) {
    cssValue = convertToCSSVar(value[cssProperty].ref);
  } else if (value[cssProperty].value) {
    if (value[cssProperty].value.startsWith("resource")) {
      cssValue = resolveResourceURL(value[cssProperty].value);
    } else {
      cssValue = value[cssProperty].value;
    }
  } else if (value[cssProperty].compute && themeModel) {
    let computeInfo = value[cssProperty].compute;
    if (value[computeInfo.ref]?.ref) {
      cssValue = computeStyles(themeModel, {
        ...computeInfo,
        // get actual reference of reffered style
        ref: value[computeInfo.ref].ref
      });
    }
  }
  return cssValue;
}

/**
 *
 * @params cssContent, domId, styleId(Optional)
 * if styleId is given then its content is updated
 * else new one is created and inserted in to specified domId
 * Check for compatibility in both cases
 */
export function prependStyleToDOM({ cssContent, domId, styleId }) {
  let styleDom = styleId && document.getElementById(styleId);

  if (styleDom) {
    if (styleDom.styleSheet) {
      // For IE versions below 8
      styleDom.styleSheet.cssText = cssContent;
    } else {
      styleDom.textContent = cssContent;
    }
  } else {
    styleDom = document.createElement("style");
    styleDom.type = "text/css";
    if (styleId) {
      styleDom.id = styleId;
    }

    if (styleDom.styleSheet) {
      // For IE versions below 8
      styleDom.styleSheet.cssText = cssContent;
    } else {
      styleDom.appendChild(document.createTextNode(cssContent));
    }

    // prepend the new styleDom to target DOM if domId is given
    // else appends to document's head
    const targetElement = document.getElementById(domId);
    if (targetElement) {
      targetElement.prepend(styleDom);
    } else {
      document.head.append(styleDom);
    }
  }
  return styleDom;
}

export function syncCustomColors(colorsList) {
  let styleString = colorsList.reduce((accumulator, colorObject) => {
    let cssVar = colorObject.Id.toLowerCase();
    cssVar = cssVar.replaceAll("_", "-");
    let value = `--kf-app-${cssVar}: ${colorObject.Color};\n`;
    return (accumulator += value);
  }, "");

  return prependStyleToDOM({
    cssContent: `body {\n${styleString}}`,
    styleId: "app-color-variables"
  });
}

export function loadCustomColors(customColors) {
  let data = {};
  customColors.forEach((item) => {
    if (item.Color) {
      data[`App.${getDotNotation(item.Id)}`] = {
        value: item.Color,
        isNew: true
      };
    }
  });
  return data;
}

export function applyAppTheme(appId, themeClass, colors, applyBackground) {
  let cssContent = "";
  if (!isEmpty(colors)) {
    const themeModel = new Theme();
    themeModel.load(colors);
    themeModel.setApplyBackground(
      colors[KEY_PRIMARY_500]?.value,
      applyBackground
    );
    Object.entries(colors).forEach(([key, obj]) => {
      themeModel.setValue(key, obj.value);
    });
    cssContent = `.${themeClass} {\n${themeModel.toCSS()}\n}`;
  } else {
    cssContent = `.${themeClass} {\n${""}\n}`;
  }

  prependStyleToDOM({
    cssContent,
    styleId: `App-${appId}-theme`
  });
}

function computeStyles(themeModel, compute) {
  let cssValue = themeModel.getValue(compute.ref);
  if (!cssValue) return;
  switch (compute.fn) {
    case COMPUTE_FUNCTION_NAMES.DARKEN:
      return darken(cssValue, compute.params[0]);
    case COMPUTE_FUNCTION_NAMES.BRIGHTEN:
      return brighten(cssValue, compute.params[0]);
    default:
      return;
  }
}

export function resolveShadowValue(value) {
  const shadowMap = {
    none: "none",
    "shadow-100": "var(--kf-shadow-100-pos)",
    "shadow-200": "var(--kf-shadow-200-pos)",
    "shadow-300": "var(--kf-shadow-300-pos)",
    "shadow-500": "var(--kf-shadow-500-pos)"
  };

  const [type, color] = value.split("|");

  if (color) return `${shadowMap[type]} ${convertToCSSVar(color)}`;

  return shadowMap[type];
}

const RESOURCE_BASE_URL = "/upload/2/";
export function resolveResourceURL(resourceUrl = "") {
  // window.location.origin - prepended before the api relative path due to api call via css (css source will got from different origin /cdn in build)
  // so api calling via css background property won't work with only using relative path api call in this case
  let url = resourceUrl.substring(
    resourceUrl.indexOf("(") + 1,
    resourceUrl.lastIndexOf(")")
  );
  return `url("${window.location.origin}${RESOURCE_BASE_URL}${getS3FileKey(
    url
  )}")`;
}
